import {
	Badge,
	Button,
	ButtonSize,
	Colour,
	ColourOutline,
	ColourStateSolid,
	FlexGap,
	FlexJustify,
	ItemRowCell,
	ItemRowSize,
	Pill,
	PillSize,
	StackHorizontal,
	StackVertical,
	useLayout,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { ItemRowSelectable } from '@ukpostbox/frontend-framework/lib/Components/ItemRow/ItemRowSelectable';
import React from 'react';
import { useNavigate } from 'react-router';
import { faFolder } from '@fortawesome/pro-solid-svg-icons/faFolder';
import { useSite } from '../../../../_Global/Providers/SiteInterfaceProvider';
import { MailTagCollection } from '../../Tags/MailTagCollection';
import { MapMailItemUrl } from '../../../Utils/MailUrlMapping';
import { useMailboxesStore } from '../../../Providers/Stores/MailboxesStore';
import { MailItemThumbnailsStacked } from '../../Thumbnails/MailItemThumbnailsStacked';
import { MailItemThumbnails } from '../../Thumbnails/MailItemThumbnails';
import { MailItemComponentType } from '../../Types/MailItemTypes';

function ListViewMailItem(props: MailItemComponentType) {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { setPdfViewer } = useSite();
	const { isTiny, isSmall, isMedium, isLarge, isXXL } = useLayout();

	const mailBoxInfo = useMailboxesStore((state) => state.mailBoxInfo);

	if (props.itemHidden) {
		return null;
	}

	const TopUpRequired = props.files[0]?.topupRequired || props.files[1]?.topupRequired;

	const HandleClick = () => {
		if (props.shiftKey?.current !== undefined && props.shiftKey.current) {
			props.callBack(props.mailUuid, !props.selected);
		} else {
			navigate(MapMailItemUrl(props.mailLocationId, props.mailUuid, props.clientFolder?.id));
		}
	};

	return (
		<ItemRowSelectable
			title={`Mail Item Selectable ${props.mailUuid}`}
			size={ItemRowSize.Large}
			highlighted={!props.dateRead}
			muted={props.dateRead !== null}
			selected={props.selected}
			extraClass='mailbox-item'
			extraProps={{
				draggable: props.draggable,
				onDragStart: (e: any) => props.onDragStart(e),
				onDragEnd: (e: any) => props.onDragEnd(e),
				onDragOver: (e: any) => props.onDragOver(e),
				'data-item-id': props.mailUuid,
			}}
			defaultChecked={props.selected}
			onChangeCallback={(e) => props.callBack(props.mailUuid, e)}
			innerExtraClass='mailbox-item-meta'
			innerExtraProps={{
				onClick: () => HandleClick(),
				draggable: false,
			}}
		>
			{isTiny ? (
				<MailItemThumbnailsStacked
					files={props.files}
					onClickCallback={(e, link) => {
						e.stopPropagation();
						setPdfViewer(true, link);
					}}
				/>
			) : (
				<MailItemThumbnails
					files={props.files}
					onClickCallback={(e, link) => {
						e.stopPropagation();
						setPdfViewer(true, link);
					}}
				/>
			)}

			<ItemRowCell extraClass='mail-item-reference'>
				{!props.dateRead && <Badge colour={Colour.Primary} />}
				<StackVertical gap={FlexGap.ExtraSmall} justify={FlexJustify.Start}>
					{props.dateRead && <span className='mail-item-tracking-id'>{props.trackingId}</span>}

					{!props.dateRead && <strong className='mail-item-tracking-id'>{props.trackingId}</strong>}

					{TopUpRequired && (
						<Pill
							content={t('mailItem.labels.topUpRequired')}
							size={PillSize.Small}
							colour={ColourOutline.Alert}
						/>
					)}

					{props.mailLocationId !== 20 && props.mailAction?.requiresUserInput && (
						<Pill content='Item Locked' size={PillSize.Small} colour={ColourOutline.Alert} />
					)}

					{!TopUpRequired && props.daysUntilNextStorageCharge && (
						<Pill
							content={t('mailItem.labels.storageCharges', { number: props.daysUntilNextStorageCharge })}
							size={PillSize.Small}
							colour={
								props?.daysUntilNextStorageCharge && props?.daysUntilNextStorageCharge <= 5
									? ColourOutline.Alert
									: ColourOutline.Secondary
							}
						/>
					)}

					{!TopUpRequired && (
						<StackHorizontal gap={FlexGap.ExtraSmall} wrap extraClass='mailbox-item-extra'>
							{props.clientFolder?.id && mailBoxInfo.customFolderId !== props.clientFolder?.id && (
								<Button
									title={props.clientFolder?.description}
									icon={faFolder}
									colour={props.clientFolder?.colour || Colour.Secondary}
									squared
									size={ButtonSize.Small}
									actionLink={{ url: `/mail/custom-folder/${props.clientFolder.id}` }}
								/>
							)}
							{props.forwardingBatchId && (
								<Button
									title='View Batch'
									colour={ColourStateSolid.Primary}
									squared
									size={ButtonSize.Small}
									actionLink={{ url: `/mail/forwarding-batches/${props.forwardingBatchId}` }}
								/>
							)}
						</StackHorizontal>
					)}
				</StackVertical>
			</ItemRowCell>

			{!isTiny && !isSmall && (
				<ItemRowCell extraClass='mail-item-pages'>
					{mailBoxInfo?.folderSlug === 'pending' && props.mailAction?.description}

					{mailBoxInfo?.folderSlug !== 'pending' && props.totalPages
						? t('counts.page', { count: props.totalPages })
						: t('mailItem.labels.unopened')}
				</ItemRowCell>
			)}

			{!isTiny && !isSmall && !isMedium && !isLarge && (
				<ItemRowCell extraClass='mail-item-tracking-numbers'>
					{props.incomingTrackingId && (
						<Pill content={`${t('mailItem.labels.trackingIdInShout')}: ${props.incomingTrackingId}`} />
					)}

					{props.outgoingTrackingId && (
						<Pill content={`${t('mailItem.labels.trackingIdOutShort')}: ${props.outgoingTrackingId}`} />
					)}
				</ItemRowCell>
			)}

			{!isTiny && !isSmall && !isMedium && (
				<ItemRowCell extraClass='mail-item-tags item-row-tags'>
					{props.tags && (
						<MailTagCollection
							mailUuid={props.mailUuid}
							tags={props.tags}
							small
							displayLimit={isXXL ? 5 : 3}
						/>
					)}
				</ItemRowCell>
			)}

			{!isTiny && !isSmall && !isMedium && !isLarge && (
				<ItemRowCell extraClass='mail-item-notes'>
					<div className='notes-content-wrapper'>{props.note}</div>
				</ItemRowCell>
			)}
		</ItemRowSelectable>
	);
}

export default ListViewMailItem;
